import Board from "@publicPortal/components/board/Board";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { RecordDetailsTabNames } from "@publicPortal/utils/enums";
import { useParams } from "react-router-dom";
import DocumentsTable from "@mirage/components/documentsTable/DocumentsTable";
import { BackButton } from "@publicPortal/components/BackButton/BackButton";
import ENoticeSummaryTab from "./ENoticeSummaryTab";
import EnforcementNoticeTabs, { ENoticeTabs, PublicEnforcementNoticeTabs } from "./EnforcementNoticeTabs";
import { IEnforcementNoticeDetails } from "@shared/publicportal/contracts/IEnforcementNoticeDetails";
import EnforcementService from "@publicPortal/services/EnforcementNoticeService";
import { ENoticeMapTab } from "./ENoticeMapTab";
import { atom, useRecoilState } from "recoil";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";
import { MobileTabPicker } from "@publicPortal/components/mobileTabPicker/MobileTabPicker";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { PPConstants } from "@shared/publicportal/constants";
import { GridColDef } from "@mui/x-data-grid-premium";

const enforcementNoticeDetailsState = atom({
    key: "enforcementNoticeDetailsState",
    default: {} as { [x: string]: ENoticeTabs },
});

const EnforcementNotice = memo(() => {
    const enforcementService = new EnforcementService();
    const { noticeId } = useParams();
    const [currentTab, setCurrentTab] = useRecoilState(enforcementNoticeDetailsState);
    const [notice, setNotice] = useState<IEnforcementNoticeDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const { isMobile } = useDeviceDetect();
    const currentApplicationTab: ENoticeTabs = notice?.id ? currentTab[notice.id] || RecordDetailsTabNames.Summary : RecordDetailsTabNames.Summary;
    const onTabSelect = useCallback(
        (newTab: string) => {
            if (!notice?.id) {
                return;
            }
            setCurrentTab((prev) => ({ ...prev, [notice.id]: newTab as RecordDetailsTabNames }));
        },
        [notice?.id, setCurrentTab]
    );
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const visibleDocumentsTableColumns = sessionValue.clientSettings.DOCUMENTS_TABLE_COLUMNS;

    useEffect(() => {
        const getNotice = async () => {
            setLoading(true);

            const response = noticeId && (await enforcementService.getEnforcementById(Number(noticeId)));
            if (!response) {
                setLoading(false);
                return;
            }
            setNotice(response);
            setLoading(false);
        };
        getNotice();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [noticeId]);

    const displayFor = useMemo(() => ({ entityRecord: { entityName: "E_NOTICE", recordId: Number(noticeId) } }), [noticeId]);
    const overrideColumns = useCallback(
        (existingColumns: GridColDef[]) => {
            if (!Array.isArray(visibleDocumentsTableColumns) || visibleDocumentsTableColumns.length == 0) {
                return existingColumns;
            }
            const alwaysVisibleColumns = ["action", "folder"];
            return existingColumns.filter((col) => visibleDocumentsTableColumns.find((visibleCol) => visibleCol == col.field || alwaysVisibleColumns.includes(col.field)));
        },
        [visibleDocumentsTableColumns]
    );

    if ((!notice || !notice.id) && !loading) {
        return (
            <>
                <ResponsiveAppBar />
                <Board headerText="Enforcement Notice not found" loadingHeaderText={loading} size="big">
                    <BackButton />
                </Board>
            </>
        );
    }

    return (
        <>
            <ResponsiveAppBar />
            <Board headerText={"Enforcement Notice " + (notice?.name || " (none)")} size="big" loadingHeaderText={loading}>
                <BackButton />
                {isMobile ? (
                    <MobileTabPicker label="Current Tab" currentTab={currentApplicationTab} tabs={PublicEnforcementNoticeTabs} onTabSelect={onTabSelect} />
                ) : (
                    <EnforcementNoticeTabs cardName={currentApplicationTab} handleChangeTab={onTabSelect} />
                )}
                {getContentForTab()}
            </Board>
        </>
    );

    function getContentForTab() {
        switch (currentApplicationTab) {
            case RecordDetailsTabNames.Summary:
                return <ENoticeSummaryTab notice={notice} loading={loading} />;
            case RecordDetailsTabNames.Documents:
                return <DocumentsTable publicOnly={true} readOnly stateKey="enforcement-notice-documents" displayFor={displayFor} overrideColumns={overrideColumns} />;
            case RecordDetailsTabNames.Map:
                return <ENoticeMapTab notice={notice} />;
            default:
                return null;
        }
    }
});

export default EnforcementNotice;
